import { SyntheticEvent, useEffect, useState } from 'react';
import { Col, Form, Row } from 'react-bootstrap';
import { FormattedMessage, useIntl } from 'react-intl';
import { useNavigate, useParams } from 'react-router';

import { useAppDispatch } from 'app/hooks';
import { doFormMsg, FormMsg, NoFormMsg } from 'components/forms/FormMessage';
import { ModalDialog, ModalDialogLink } from 'components/modal/Modal';
import { useModal } from 'components/modal/ModalContext';
import { login } from 'features/auth/authSlice';
import { SLUGS } from 'helpers/consts';
import { getPattern } from 'helpers/regex';
import { useCurrentSlug, useRestrictedRoute } from 'hooks/useCurrentSlug';
import { ShowPasswordButton } from './ShowPasswordButton';
import UserForgotForm from './UserForgotForm';
import UserSignupForm from './UserSignupForm';

import './UserForm.scss';

type UserLoginFormProps = {
  // slug?: string; // Can be any slug string or undefined (passed from Layout)
};

export default function UserLoginForm(props: UserLoginFormProps) {
  const intl = useIntl();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [data, setData] = useState({
    email: '',
    password: '',
  });
  const { closeModal } = useModal();
  const { code } = useParams();
  // Prefer slug from props (passed by Layout), fallback to extracting from current location
  // const slug = props.slug ?? useCurrentSlug();
  const slug = useCurrentSlug();
  const isRestrictedRoute = useRestrictedRoute();
  const [validForm, setValidForm] = useState<boolean>(true);
  const [formMsg, setFormMsg] = useState<FormMsg>(NoFormMsg);
  const [showPassword, setShowPassword] = useState<boolean>(false);
  // Track if form was opened while on a restricted route
  const [openedOnRestrictedRoute] = useState<boolean>(isRestrictedRoute);

  // Close the modal if we navigate away from a restricted route
  useEffect(() => {
    if (openedOnRestrictedRoute && !isRestrictedRoute) {
      closeModal();
    }
  }, [isRestrictedRoute, openedOnRestrictedRoute, closeModal]);

  function handleChangeData(e: SyntheticEvent) {
    const target = e.target as HTMLInputElement;
    setData({
      ...data,
      [target.name]: target.value,
    });
  }

  async function handleSubmit(): Promise<boolean> {
    return new Promise(async (resolve, reject) => {
      try {
        await dispatch(login(data)).then(async (response: { payload: any }) => {
          switch (response.payload?.response.status) {
            case 200:
            case 201:
              closeModal();
              // Handle navigation based on context
              if (code) {
                navigate('/join/' + code);
              } else if (!slug || slug === SLUGS.FRONT) {
                // navigate(`/${SLUGS.MYPAGE}`);
                navigate(`/`);
              }
              resolve(true);
              break;
            case 423:
              setValidForm(false);
              doFormMsg(
                {
                  message: intl.formatMessage({ id: 'USERS.LOGIN.LOCKED' }),
                  success: false,
                },
                setFormMsg,
              );
              resolve(false);
              break;
            case 403:
              setValidForm(false);
              doFormMsg(
                {
                  message: intl.formatMessage({
                    id: 'USERS.LOGIN.UNVERIFIED',
                  }),
                  success: false,
                },
                setFormMsg,
              );
              resolve(false);
              break;
            case 401:
            default:
              setValidForm(false);
              doFormMsg(
                {
                  message: intl.formatMessage({ id: 'USERS.LOGIN.FAILED' }),
                  success: false,
                },
                setFormMsg,
              );
              resolve(false);
              break;
          }
        });
      } catch (err) {
        setValidForm(false);
        doFormMsg(
          {
            message: intl.formatMessage({ id: 'X.FAILED_UNKNOWN' }),
            success: false,
          },
          setFormMsg,
        );
        reject(intl.formatMessage({ id: 'X.FAILED_UNKNOWN' }));
      }
    });
  }

  return (
    <ModalDialog
      className="user_form narrow"
      title={<FormattedMessage id="USERS.LOGIN.FORM_TITLE" />}
      okBtn={{
        label: intl.formatMessage({ id: 'USERS.LOGIN.SUBMIT' }),
        onClick: handleSubmit,
      }}
      enableEscape
      formMsg={formMsg}
      footerRight={
        <ModalDialogLink
          component={UserSignupForm}
          asButton
          onClick={closeModal}
          closeAll
        >
          <FormattedMessage id="USERS.LOGIN.NEW_USER" />
        </ModalDialogLink>
      }
      validated={!validForm}
      setValidForm={setValidForm}
    >
      <Form.Group as={Row} controlId="Email">
        <Form.Label column sm="12" md="12" lg="12">
          <FormattedMessage id="USERS.LOGIN.EMAIL" />
        </Form.Label>
        <Col sm={12} md={12} lg={12}>
          <Form.Control
            type="email"
            name="email"
            autoComplete="username email"
            value={data.email || ''}
            onChange={handleChangeData}
            required
            pattern={getPattern('email')}
          />
          <Form.Control.Feedback type="invalid">
            <FormattedMessage id="USERS.LOGIN.EMAIL_FDBCK" />
          </Form.Control.Feedback>
        </Col>
      </Form.Group>
      <Form.Group as={Row} controlId="Password">
        <Form.Label column sm="12" md="12" lg="12">
          <FormattedMessage id="USERS.LOGIN.PASSWORD" />
        </Form.Label>
        <Col sm={12} md={12} lg={12}>
          <Row className="subrow">
            <Form.Control
              type={showPassword ? 'text' : 'password'}
              name="password"
              autoComplete="password"
              value={data.password || ''}
              onChange={handleChangeData}
              required
            />
            <ShowPasswordButton
              showPassword={showPassword}
              setShowPassword={setShowPassword}
            />
          </Row>
          <Form.Control.Feedback type="invalid">
            <FormattedMessage id="USERS.LOGIN.PASSWORD_FDBCK" />
          </Form.Control.Feedback>
        </Col>
      </Form.Group>
      <Form.Group as={Row} className="form">
        <Col align={'left'} sm={12}>
          <div className="vspaced">
            <ModalDialogLink component={UserForgotForm} closeAll>
              <FormattedMessage id="USERS.LOGIN.FORGOT" />
            </ModalDialogLink>
          </div>
        </Col>
      </Form.Group>
    </ModalDialog>
  );
}
